
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Lightbulb } from "lucide-react";

interface QuestionHintButtonProps {
  questionId: string | number;
  subject: string;
}

const QuestionHintButton: React.FC<QuestionHintButtonProps> = ({
  questionId,
  subject
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const getHint = async () => {
    try {
      setIsLoading(true);
      
      // Call the AI tutoring edge function to generate a hint
      const response = await fetch('/.netlify/functions/ai-tutoring', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get-hint',
          data: {
            questionId: questionId,
            subject: subject
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get hint');
      }
      
      const data = await response.json();
      
      if (data && data.hint) {
        toast.success('Hint', {
          description: data.hint,
          duration: 5000
        });
      } else {
        throw new Error('No hint data received');
      }
    } catch (error) {
      console.error('Error getting hint:', error);
      toast.error('Hint not available for this question', {
        description: "Try another question or contact support if this problem persists.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      onClick={getHint}
      disabled={isLoading}
      className="flex items-center gap-2 bg-amber-500 text-white border-amber-600
        hover:bg-amber-600 hover:text-white hover:border-amber-700
        active:bg-amber-700 active:border-amber-800
        disabled:bg-gray-300 disabled:text-gray-600 disabled:border-gray-400"
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Lightbulb className="w-4 h-4" />
      )}
      {isLoading ? "Loading hint..." : "Show Hint"}
    </Button>
  );
};

export default QuestionHintButton;
